import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import {
  applySpec,
  assoc,
  compose,
  curry,
  equals,
  flip,
  head,
  ifElse,
  includes,
  isEmpty,
  keys,
  last,
  map,
  reduce,
  reject,
  replace,
  path,
  split,
  toPairs,
  __,
} from 'ramda';
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import Checkbox from '@material-ui/core/Checkbox';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import { stylesToPropType } from '../../utils';

const BASE_SCOPE = 'client';

const KNOWN_SCOPE_ABBREVIATIONS = Object.freeze({
  idm: 'Identity Manager',
  ntm: 'Notification Manager',
  em: 'Event Manager',
  doc: 'Document Store',
  eks: 'Encrypted Keystore',
  cour: 'Courier Messaging Service',
});

const BASE_SCOPE_MODEL = Object.freeze(reduce(
  flip(assoc(__, { read: false, write: false }, __)),
  {},
  keys(KNOWN_SCOPE_ABBREVIATIONS),
));

const inflateScope = applySpec({
  abbreviation: compose(last, split(':')),
  read: compose(includes('read'), head, split(':')),
  write: compose(includes('write'), head, split(':')),
});

const buildScopeModel = reduce(
  (accum, { abbreviation, read, write }) => assoc(abbreviation, { read, write }, accum),
  BASE_SCOPE_MODEL,
);

const updateScopeString = curry((abbreviation, read, write, scopeString) => {
  const newScope = `${read ? 'read' : ''}${write ? 'write' : ''}:${abbreviation}`;

  return includes(`:${abbreviation}`, scopeString)
    ? replace(new RegExp(`(read|write|readwrite)?:${abbreviation}`), newScope, scopeString)
    : `${scopeString} ${newScope}`;
});

const consolidateSpaces = replace(/( )+/g, ' ');

const styles = () => ({
  disabled: {
    '& *': {
      color: '#ccc',
      cursor: 'default',
    },
    '& input': {
      pointerEvents: 'none',
    },
  },
});

const UnstyledScopeEditor = ({
  classes,
  scopeString,
  onChange,
}) => {
  const scopes = reject(isEmpty, split(' ', scopeString));
  const baseScopeEnabled = includes(BASE_SCOPE, scopes);
  const inflatedScopes = map(inflateScope, reject(equals(BASE_SCOPE), scopes));
  const scopeModel = buildScopeModel(inflatedScopes);
  const scopeModelPairs = toPairs(scopeModel);

  return (
    <Fragment>
      <FormControlLabel
        control={(
          <Switch
            checked={baseScopeEnabled}
            onChange={compose(
              onChange,
              consolidateSpaces,
              ifElse(
                path(['target', 'checked']),
                () => `${BASE_SCOPE} ${scopeString}`,
                () => replace(BASE_SCOPE, '', scopeString),
              ),
            )}
            value="enable-base-scope"
            color="primary"
          />
        )}
        label="enable"
      />

      <Table className={baseScopeEnabled ? '' : classes.disabled}>
        <TableHead>
          <TableRow>
            <TableCell colSpan="4">Scope</TableCell>
            <TableCell>Read</TableCell>
            <TableCell>Write</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {map(([abbreviation, { read, write }]) => (
            <TableRow key={abbreviation}>
              <TableCell colSpan="4">{KNOWN_SCOPE_ABBREVIATIONS[abbreviation] || abbreviation}</TableCell>
              <TableCell>
                <Checkbox
                  checked={read}
                  color="default"
                  value={`${abbreviation}-read`}
                  onChange={compose(
                    onChange,
                    consolidateSpaces,
                    updateScopeString(abbreviation, __, write, scopeString),
                    path(['target', 'checked']),
                  )}
                />
              </TableCell>
              <TableCell>
                <Checkbox
                  checked={write}
                  color="default"
                  value={`${abbreviation}-write`}
                  onChange={compose(
                    onChange,
                    consolidateSpaces,
                    updateScopeString(abbreviation, read, __, scopeString),
                    path(['target', 'checked']),
                  )}
                />
              </TableCell>
            </TableRow>
          ), scopeModelPairs)}
        </TableBody>
      </Table>
    </Fragment>
  );
};

UnstyledScopeEditor.propTypes = {
  classes: stylesToPropType(styles()).isRequired,
  scopeString: PropTypes.string.isRequired,
  onChange: PropTypes.func,
};

UnstyledScopeEditor.defaultProps = {
  onChange: () => {},
};

export default withStyles(styles)(UnstyledScopeEditor);
